import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: t('accueil') },
    { href: "/services", label: t('services') },
    { href: "/zones-intervention", label: "Zones d'intervention" },
    { href: "/engagement-rse", label: t('engagementRse') },
    { href: "/a-propos", label: t('aPropos') },
    { href: "/postuler", label: t('postuler') },
    { href: "/contact", label: t('contact') },
  ];

  const isActive = (path) => location.pathname === path || (path === "/services" && location.pathname.startsWith("/services"));

  const languages = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'nl', label: 'NL' }
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg"
          : "bg-white/95 backdrop-blur-md shadow-sm"
      }`}
    >
      {/* Top bar with language selector */}
      <div className={`border-b transition-colors duration-300 ${isScrolled ? 'border-slate-100' : 'border-slate-100'}`}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-end h-8 text-xs">
            {/* Language selector - Right */}
            <div className="flex items-center gap-1">
              {languages.map((lang, index) => (
                <span key={lang.code} className="flex items-center">
                  <button
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-2 py-1 rounded transition-colors font-medium ${
                      language === lang.code
                        ? 'bg-[#1E3A8A] text-white'
                        : 'text-slate-500 hover:text-[#1E3A8A] hover:bg-slate-100'
                    }`}
                  >
                    {lang.label}
                  </button>
                  {index < languages.length - 1 && (
                    <span className="mx-1 text-slate-300">|</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_affiche-display/artifacts/crzax0bb_Nettis%20transparent.png"
              alt="Nettis Facility"
              className="h-[60px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-[#1E3A8A]"
                    : "text-slate-600 hover:text-[#1E3A8A]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+3228806650"
              data-testid="phone-link"
              className="flex items-center gap-2 text-sm transition-colors text-slate-600 hover:text-[#1E3A8A]"
            >
              <Phone size={18} />
              <span>+32 2 880 66 50</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-x-0 top-24 bg-white shadow-xl transition-all duration-300 max-h-[80vh] overflow-y-auto ${
          isMobileMenuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="container mx-auto px-4 py-6 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-base font-medium py-2 transition-colors ${
                isActive(link.href)
                  ? "text-[#1E3A8A]"
                  : "text-slate-600"
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
            <a
              href="tel:+3228806650"
              className="flex items-center gap-2 text-slate-600"
            >
              <Phone size={18} />
              <span>+32 2 880 66 50</span>
            </a>
          </div>
          {/* Mobile language selector */}
          <div className="pt-4 border-t border-slate-100 flex items-center gap-2">
            <span className="text-sm text-slate-500">{t('langue')}:</span>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === lang.code
                    ? 'bg-[#1E3A8A] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
